import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllSheets } from "../Redux/Actions/TechnicalSheet";
import { Table, Button, Modal, Form, Spinner, Alert } from "react-bootstrap";
import "./ListTechnicalSheets.css";
import { fetchProducts } from "../Redux/Actions/productActions";
import { getFamily } from "../Redux/Actions/IngredientActions";
import jsPDF from "jspdf";
import "jspdf-autotable";
import axios from "axios";

function ListTechnicalSheets() {
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editableRows, setEditableRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [totalCost, setTotalCost] = useState(0);
  
  // État pour l'ajout d'une nouvelle ligne
  const [showAddRow, setShowAddRow] = useState(false);
  const [newRow, setNewRow] = useState({
    family: '',
    ingredient: '',
    quantity: 0,
    pricePerUnit: 0,
    cost: 0,
    ingredients: [] // Pour stocker les ingrédients de la famille sélectionnée
  });

  // Récupération des données depuis Redux
  const families = useSelector((state) => state.IngredientReducer?.families || []);
  const products = useSelector((state) => state.productReducer?.products || []);
  const technicalSheets = useSelector((state) => state.TechnicalSheetReducer?.sheets || []);

  // Rafraîchir les données après les modifications
  const refreshData = async () => {
    try {
      console.log("🔄 Rafraîchissement des données...");
      await dispatch(getAllSheets());
      console.log("✅ Données rafraîchies");
    } catch (error) {
      console.error("Erreur lors du rafraîchissement des données:", error);
    }
  };

  // Calculer le coût total à partir des lignes éditées
  const calculateTotalCost = (rows) => {
    return rows.reduce((total, row) => {
      const rowCost = (row.quantity || 0) * (row.pricePerUnit || 0);
      return total + (rowCost || 0);
    }, 0);
  };

  // Mettre à jour le coût total quand les lignes changent
  useEffect(() => {
    if (editableRows.length > 0) {
      const newTotalCost = calculateTotalCost(editableRows);
      setTotalCost(newTotalCost);
    }
  }, [editableRows]);

  // Charger les données initiales avec gestion d'erreur
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log("🔄 Chargement des données...");
        setError(null);
        
        // Charger les familles (qui contiennent les ingrédients)
        await dispatch(getFamily());
        console.log("✅ Familles chargées");
        
        // Charger les fiches techniques
        try {
          await dispatch(getAllSheets());
        } catch (sheetError) {
          console.warn("⚠️ Erreur lors du chargement des fiches:", sheetError);
        }
        
        // Charger les produits
        try {
          await dispatch(fetchProducts());
        } catch (productError) {
          console.warn("⚠️ Erreur lors du chargement des produits:", productError);
        }
        
        // Charger les localisations
        try {
          await fetchLocations();
        } catch (locationError) {
          console.warn("⚠️ Erreur lors du chargement des localisations:", locationError);
        }
        
        console.log("✅ Données de base chargées");
        
      } catch (error) {
        console.error("❌ Erreur chargement:", error);
        setError("Erreur lors du chargement des données. Vérifiez votre connexion.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dispatch]);

  // Charger les localisations avec gestion d'erreur
  const fetchLocations = async () => {
    try {
      const res = await axios.get("/api/Locationspin/getAllLocations", {
        timeout: 10000
      });
      setLocations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erreur lors du chargement des emplacements :", err);
      setLocations([
        { _id: "68fe82cd5a28e993bc70dbc3", name: "Spintoss-Les Jardins" },
        { _id: "68fe837c5a28e993bc70dbc4", name: "Spintoss-Tunisia Mall" },
        { _id: "6947e810cf2f41fc52fca859", name: "Spintoss-Spintoss FAB" }
      ]);
    }
  };

  // Mettre à jour les données de la nouvelle ligne
  const handleNewRowChange = (field, value) => {
    const updatedRow = { ...newRow };
    
    if (field === 'family') {
      const selectedFamily = families.find((fam) => fam._id === value);
      updatedRow.family = value;
      updatedRow.ingredients = selectedFamily ? selectedFamily.ingredients : [];
      updatedRow.ingredient = '';
      updatedRow.pricePerUnit = 0;
      updatedRow.cost = 0;
    } 
    else if (field === 'ingredient') {
      updatedRow.ingredient = value;
      // Récupérer le prix d'achat de l'ingrédient si disponible
      const selectedIngredient = updatedRow.ingredients.find(ing => ing._id === value);
      if (selectedIngredient && selectedIngredient.purchasePrice) {
        updatedRow.pricePerUnit = selectedIngredient.purchasePrice;
      }
      updatedRow.cost = (updatedRow.quantity || 0) * (updatedRow.pricePerUnit || 0);
    }
    else if (field === 'quantity') {
      updatedRow.quantity = parseFloat(value) || 0;
      updatedRow.cost = updatedRow.quantity * (updatedRow.pricePerUnit || 0);
    }
    else if (field === 'pricePerUnit') {
      updatedRow.pricePerUnit = parseFloat(value) || 0;
      updatedRow.cost = (updatedRow.quantity || 0) * updatedRow.pricePerUnit;
    }
    
    setNewRow(updatedRow);
  };

  // Fonction pour obtenir le nom de la localisation (avec fallback)
  const getLocationName = (locationData) => {
    try {
      if (!locationData) return "Non spécifiée";
      
      if (typeof locationData === 'object' && locationData.name) {
        return locationData.name;
      }
      
      const locationId = locationData._id || locationData;
      
      if (locations.length > 0) {
        const location = locations.find(loc => {
          return loc && (loc._id === locationId || loc.id === locationId);
        });
        
        if (location) return location.name;
      }
      
      const defaultLocations = {
        "68fe82cd5a28e993bc70dbc3": "Spintoss-Les Jardins",
        "68fe837c5a28e993bc70dbc4": "Spintoss-Tunisia Mall", 
        "6947e810cf2f41fc52fca859": "Spintoss-Spintoss FAB"
      };
      
      return defaultLocations[locationId] || "Localisation inconnue";
    } catch (error) {
      return "Localisation inconnue";
    }
  };

  // Fonction pour obtenir le nom du produit
  const getProductNameById = (productId) => {
    try {
      if (!productId) return "Produit inconnu";
      
      if (products && products.length > 0) {
        const product = products.find((prod) => prod._id === productId);
        return product?.name || "Produit inconnu";
      }
      
      return "Produit inconnu";
    } catch (error) {
      return "Produit inconnu";
    }
  };

  // Fonction pour obtenir le nom du sous-produit
  const getSubProductNameById = (productId, subProductId) => {
    try {
      if (!productId || !subProductId) return "Sous-produit inconnu";
      
      const product = products.find((prod) => prod._id === productId);
      if (!product || !product.subProducts) return "Sous-produit inconnu";
      
      const subProduct = product.subProducts.find(
        (sub) => sub._id === subProductId
      );
      return subProduct?.name || "Sous-produit inconnu";
    } catch (error) {
      return "Sous-produit inconnu";
    }
  };

  // Fonction pour obtenir le nom de l'ingrédient
  const getIngredientNameById = (row) => {
    try {
      if (!row) return "Non spécifié";

      if (row.ingredient?.name) return row.ingredient.name;

      if (row.ingredient) {
        for (const family of families) {
          if (family.ingredients && Array.isArray(family.ingredients)) {
            const ing = family.ingredients.find(
              (i) => i?._id?.toString() === row.ingredient.toString()
            );
            if (ing) return ing.name;
          }
        }
        return "Ingrédient inconnu";
      }

      return "Non spécifié";
    } catch (error) {
      return "Erreur";
    }
  };

  // Fonction pour obtenir le nom de la famille
  const getFamilyName = (familyData) => {
    try {
      if (!familyData) return "Non spécifiée";
      if (typeof familyData === "object" && familyData.name) return familyData.name;

      const family = families.find((fam) => fam._id === familyData);
      return family?.name || "Famille inconnue";
    } catch (error) {
      return "Erreur";
    }
  };

  const getSubProductWithLocation = (sheet) => {
    const subProductName = getSubProductNameById(sheet.product, sheet.subProduct);
    const locationName = getLocationName(sheet.localisation);
    return `${subProductName} (${locationName})`;
  };

  // Fonctions pour les modifications existantes
  const handlePriceChange = (index, newPrice) => {
    const updatedRows = [...editableRows];
    updatedRows[index].pricePerUnit = parseFloat(newPrice) || 0;
    updatedRows[index].cost = updatedRows[index].quantity * updatedRows[index].pricePerUnit;
    setEditableRows(updatedRows);
    
    // Mettre à jour le coût total automatiquement
    const newTotalCost = calculateTotalCost(updatedRows);
    setTotalCost(newTotalCost);
  };

  const handleQuantityChange = (index, newQuantity) => {
    const updatedRows = [...editableRows];
    updatedRows[index].quantity = parseFloat(newQuantity) || 0;
    updatedRows[index].cost = updatedRows[index].quantity * (updatedRows[index].pricePerUnit || 0);
    setEditableRows(updatedRows);
    
    // Mettre à jour le coût total automatiquement
    const newTotalCost = calculateTotalCost(updatedRows);
    setTotalCost(newTotalCost);
  };

  const showDetails = (sheet) => {
    console.log("🔍 Sélection de la fiche:", sheet);
    setSelectedSheet(sheet);
    setEditableRows([...sheet.rows]);
    
    // Calculer le coût total initial
    const initialTotalCost = calculateTotalCost(sheet.rows);
    setTotalCost(initialTotalCost);
    
    setShowModal(true);
    setShowAddRow(false);
    setNewRow({
      family: '',
      ingredient: '',
      quantity: 0,
      pricePerUnit: 0,
      cost: 0,
      ingredients: []
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Supprimer cette fiche technique ?")) {
      try {
        const response = await axios.delete(`/api/TechnicalSheet/${id}`, {
          timeout: 10000
        });

        if (response.status === 200) {
          await refreshData();
          alert("Fiche technique supprimée.");
        } else {
          alert("Erreur suppression.");
        }
      } catch (error) {
        console.error("Erreur suppression:", error);
        alert(`Erreur: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  // Fonction pour ajouter une nouvelle ligne
  const handleAddNewRow = async () => {
    let ingredientObj = null;
    let familyId = null;
    
    try {
      if (!selectedSheet || !selectedSheet._id) {
        alert("Aucune fiche technique sélectionnée.");
        return;
      }

      if (!newRow.ingredient || !newRow.quantity || newRow.quantity <= 0) {
        alert("Veuillez sélectionner un ingrédient et saisir une quantité valide.");
        return;
      }

      if (!newRow.pricePerUnit || newRow.pricePerUnit <= 0) {
        alert("Veuillez saisir un prix unitaire valide (supérieur à 0).");
        return;
      }

      // Trouver l'ingrédient sélectionné dans les familles
      ingredientObj = null;
      familyId = null;
      
      for (const family of families) {
        if (family.ingredients && Array.isArray(family.ingredients)) {
          const ing = family.ingredients.find(
            (i) => i?._id?.toString() === newRow.ingredient.toString()
          );
          if (ing) {
            ingredientObj = ing;
            familyId = family._id;
            break;
          }
        }
      }
      
      if (!ingredientObj) {
        alert("Ingrédient non trouvé.");
        return;
      }

      const cost = newRow.quantity * newRow.pricePerUnit;
      
      const rowData = {
        ingredient: ingredientObj._id,
        family: familyId,
        quantity: parseFloat(newRow.quantity),
        pricePerUnit: parseFloat(newRow.pricePerUnit),
        cost: parseFloat(cost.toFixed(3)),
      };

      setIsSaving(true);
      const response = await axios.post(`/api/TechnicalSheet/add-row/${selectedSheet._id}`, rowData, {
        timeout: 10000
      });

      if (response.status === 200 || response.status === 201) {
        // Ajouter la nouvelle ligne localement pour une mise à jour immédiate
        const newRowWithDetails = {
          ...rowData,
          ingredient: {
            _id: ingredientObj._id,
            name: ingredientObj.name
          },
          family: familyId,
          _id: `temp_${Date.now()}` // ID temporaire
        };
        
        const updatedRows = [...editableRows, newRowWithDetails];
        setEditableRows(updatedRows);
        
        // Mettre à jour le coût total
        const newTotalCost = calculateTotalCost(updatedRows);
        setTotalCost(newTotalCost);
        
        // Rafraîchir les données depuis le serveur
        await refreshData();
        
        // Mettre à jour le modal avec les nouvelles données du serveur si disponibles
        if (response.data && response.data.sheet) {
          setSelectedSheet(response.data.sheet);
          setEditableRows([...response.data.sheet.rows]);
          const serverTotalCost = calculateTotalCost(response.data.sheet.rows);
          setTotalCost(serverTotalCost);
        }
        
        setShowAddRow(false);
        setNewRow({
          family: '',
          ingredient: '',
          quantity: 0,
          pricePerUnit: 0,
          cost: 0,
          ingredients: []
        });

        alert("✅ Nouvelle ligne ajoutée avec succès !");
      } else {
        alert("❌ Erreur lors de l'ajout de la ligne.");
      }

    } catch (error) {
      console.error("❌ Erreur lors de l'ajout de la ligne:", error);
      alert(`❌ Erreur: ${error.response?.data?.message || error.message || "Veuillez réessayer"}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Fonction pour supprimer une ligne
  const handleDeleteRow = async (rowId) => {
    if (!selectedSheet || !rowId) return;
    
    if (window.confirm("Supprimer cette ligne de la fiche technique ?")) {
      try {
        setIsSaving(true);
        const response = await axios.delete(`/api/TechnicalSheet/delete-row/${selectedSheet._id}/${rowId}`, {
          timeout: 10000
        });

        if (response.status === 200) {
          // Supprimer la ligne localement pour une mise à jour immédiate
          const updatedRows = editableRows.filter(row => row._id !== rowId);
          setEditableRows(updatedRows);
          
          // Mettre à jour le coût total
          const newTotalCost = calculateTotalCost(updatedRows);
          setTotalCost(newTotalCost);
          
          alert("Ligne supprimée avec succès.");
          
          // Rafraîchir les données depuis le serveur
          await refreshData();
          
          // Mettre à jour le modal si disponible
          if (response.data && response.data.sheet) {
            setSelectedSheet(response.data.sheet);
            setEditableRows([...response.data.sheet.rows]);
            const serverTotalCost = calculateTotalCost(response.data.sheet.rows);
            setTotalCost(serverTotalCost);
          }
        } else {
          alert("Erreur lors de la suppression.");
        }
      } catch (error) {
        console.error("Erreur suppression ligne:", error);
        alert(`Erreur: ${error.response?.data?.message || error.message}`);
      } finally {
        setIsSaving(false);
      }
    }
  };

  // Générer PDF
  const generatePDF = (sheet) => {
    try {
      const productName = getProductNameById(sheet.product);
      const subProductName = getSubProductNameById(sheet.product, sheet.subProduct);
      const locationName = getLocationName(sheet.localisation);
      
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      doc.setFontSize(20);
      doc.setTextColor(40, 40, 40);
      doc.text("FICHE TECHNIQUE", 105, 20, { align: "center" });

      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Générée le ${new Date().toLocaleDateString("fr-FR")}`, 105, 30, { align: "center" });

      doc.setFontSize(14);
      doc.setTextColor(0, 0, 150);
      doc.text("INFORMATIONS DU PRODUIT", 14, 45);

      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.text(`Produit: ${productName}`, 14, 55);
      doc.text(`Sous-produit: ${subProductName}`, 14, 62);
      doc.text(`Localisation: ${locationName}`, 14, 69);
      doc.text(`Coût total: ${sheet.totalCost?.toFixed(2)} DT`, 14, 76);

      doc.setFontSize(14);
      doc.setTextColor(0, 100, 0);
      doc.text("COMPOSITION", 14, 90);

      const tableData = sheet.rows?.map((row, index) => {
        const ingredientName = getIngredientNameById(row);
        const familyName = getFamilyName(row.family);
        const cost = row.quantity * (row.pricePerUnit || 0);
        
        return [
          index + 1,
          ingredientName,
          familyName,
          row.quantity,
          `${row.pricePerUnit?.toFixed(3) || '0.000'} DT`,
          `${cost.toFixed(3)} DT`
        ];
      }) || [];

      doc.autoTable({
        startY: 95,
        head: [["#", "Ingrédient", "Famille", "Quantité", "Prix Unitaire", "Coût"]],
        body: tableData,
        theme: "grid",
        headStyles: { fillColor: [0, 100, 0], textColor: 255 },
        margin: { top: 2 },
        styles: { fontSize: 10 },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 50 },
          2: { cellWidth: 40 },
          3: { cellWidth: 25 },
          4: { cellWidth: 30 },
          5: { cellWidth: 25 }
        }
      });

      const fileName = `Fiche_Technique_${productName.replace(/\s+/g, '_')}_${subProductName.replace(/\s+/g, '_')}.pdf`;
      doc.save(fileName);
      alert("✅ PDF généré avec succès !");

    } catch (error) {
      console.error("❌ Erreur génération PDF:", error);
      alert("❌ Erreur lors de la génération du PDF.");
    }
  };

  // Fonction pour télécharger toutes les fiches en PDF
  const downloadAllPDFs = () => {
    try {
      if (technicalSheets.length === 0) {
        alert("Aucune fiche technique à télécharger.");
        return;
      }

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      let currentPage = 1;
      
      technicalSheets.forEach((sheet, index) => {
        if (index > 0) {
          doc.addPage();
          currentPage++;
        }

        const productName = getProductNameById(sheet.product);
        const subProductName = getSubProductNameById(sheet.product, sheet.subProduct);
        const locationName = getLocationName(sheet.localisation);

        doc.setFontSize(16);
        doc.setTextColor(40, 40, 40);
        doc.text(`FICHE TECHNIQUE N°${index + 1}`, 105, 20, { align: "center" });

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Page ${currentPage}/${technicalSheets.length}`, 105, 28, { align: "center" });

        doc.setFontSize(12);
        doc.setTextColor(0, 0, 150);
        doc.text("INFORMATIONS DU PRODUIT", 14, 40);

        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(`Produit: ${productName}`, 14, 48);
        doc.text(`Sous-produit: ${subProductName}`, 14, 53);
        doc.text(`Localisation: ${locationName}`, 14, 58);
        doc.text(`Coût total: ${sheet.totalCost?.toFixed(2)} DT`, 14, 63);

        const tableData = sheet.rows?.map((row, rowIndex) => {
          const ingredientName = getIngredientNameById(row);
          const familyName = getFamilyName(row.family);
          const cost = row.quantity * (row.pricePerUnit || 0);
          
          return [
            rowIndex + 1,
            ingredientName,
            familyName,
            row.quantity,
            `${row.pricePerUnit?.toFixed(3) || '0.000'} DT`,
            `${cost.toFixed(3)} DT`
          ];
        }) || [];

        doc.autoTable({
          startY: 70,
          head: [["#", "Ingrédient", "Famille", "Quantité", "Prix Unitaire", "Coût"]],
          body: tableData,
          theme: "grid",
          headStyles: { fillColor: [0, 100, 0], textColor: 255 },
          margin: { top: 2 },
          styles: { fontSize: 9 },
          columnStyles: {
            0: { cellWidth: 10 },
            1: { cellWidth: 45 },
            2: { cellWidth: 35 },
            3: { cellWidth: 20 },
            4: { cellWidth: 25 },
            5: { cellWidth: 25 }
          }
        });
      });

      const fileName = `Toutes_les_Fiches_Techniques_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      alert(`✅ PDF généré avec succès ! ${technicalSheets.length} fiches incluses.`);

    } catch (error) {
      console.error("❌ Erreur génération PDF complet:", error);
      alert("❌ Erreur lors de la génération du PDF complet.");
    }
  };

  // Sauvegarder les modifications de prix
  const savePriceUpdates = async () => {
    try {
      if (!selectedSheet || !selectedSheet._id) {
        alert("Aucune fiche technique sélectionnée.");
        return;
      }

      setIsSaving(true);
      
      // Inclure le coût total mis à jour dans la requête
      const updatedRows = editableRows.map(row => ({
        _id: row._id || row.id,
        ingredient: row.ingredient?._id || row.ingredient,
        family: row.family?._id || row.family,
        quantity: parseFloat(row.quantity) || 0,
        pricePerUnit: parseFloat(row.pricePerUnit) || 0,
        cost: (parseFloat(row.quantity) || 0) * (parseFloat(row.pricePerUnit) || 0)
      }));

      // Calculer le nouveau coût total
      const newTotalCost = calculateTotalCost(editableRows);

      const response = await axios.put(`/api/TechnicalSheet/${selectedSheet._id}`, {
        rows: updatedRows,
        totalCost: newTotalCost // Inclure le coût total mis à jour
      }, {
        timeout: 10000
      });

      if (response.status === 200) {
        alert("✅ Modifications enregistrées avec succès !");
        setShowModal(false);
        
        // Rafraîchir les données pour obtenir la version à jour du serveur
        await refreshData();
      } else {
        alert(`❌ Erreur: ${response.data?.message || "Mise à jour échouée"}`);
      }
    } catch (error) {
      console.error("❌ Erreur mise à jour :", error);
      alert(`❌ Erreur: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Fonction pour annuler l'ajout et réinitialiser le formulaire
  const handleCancelAddRow = () => {
    setShowAddRow(false);
    setNewRow({
      family: '',
      ingredient: '',
      quantity: 0,
      pricePerUnit: 0,
      cost: 0,
      ingredients: []
    });
  };

  if (loading) {
    return (
      <div className="lts-loading">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
        <p className="mt-3">Chargement des fiches techniques...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="lts-container">
        <Alert variant="danger">
          <Alert.Heading>Erreur de chargement</Alert.Heading>
          <p>{error}</p>
          <hr />
          <p className="mb-0">
            Vérifiez votre connexion internet et assurez-vous que le serveur backend fonctionne.
          </p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="lts-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>📋 Liste des Fiches Techniques</h2>
        {technicalSheets.length > 0 && (
          <Button 
            variant="success" 
            onClick={downloadAllPDFs}
            className="lts-btn lts-btn-success"
          >
            <span className="lts-btn-icon">📥</span>
            <span className="lts-btn-text">Télécharger toutes les fiches (PDF)</span>
          </Button>
        )}
      </div>

      {technicalSheets.length > 0 ? (
        <div className="lts-table-wrapper">
          <Table striped bordered hover responsive className="lts-table">
            <thead className="table-dark">
              <tr>
                <th>#</th>
                <th>Produit</th>
                <th>Sous-produit (Localisation)</th>
                <th>Coût total</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {technicalSheets.map((sheet, index) => (
                <tr key={sheet._id}>
                  <td className="fw-bold">{index + 1}</td>
                  <td>{getProductNameById(sheet.product)}</td>
                  <td>
                    {getSubProductWithLocation(sheet)}
                  </td>
                  <td className="fw-bold text-primary">{sheet.totalCost?.toFixed(2)} DT</td>
                  <td>
                    <div className="lts-action-buttons">
                      <Button 
                        variant="info" 
                        size="sm"
                        onClick={() => showDetails(sheet)}
                        className="lts-btn lts-btn-sm lts-btn-info"
                      >
                        <span className="lts-btn-icon">👁️</span>
                        <span className="lts-btn-text">Détails</span>
                      </Button>
                      <Button 
                        variant="warning" 
                        size="sm"
                        onClick={() => generatePDF(sheet)}
                        className="lts-btn lts-btn-sm lts-btn-warning"
                      >
                        <span className="lts-btn-icon">📄</span>
                        <span className="lts-btn-text">PDF</span>
                      </Button>
                      <Button 
                        variant="danger" 
                        size="sm"
                        onClick={() => handleDelete(sheet._id)}
                        className="lts-btn lts-btn-sm lts-btn-danger"
                      >
                        <span className="lts-btn-icon">🗑️</span>
                        <span className="lts-btn-text">Supprimer</span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="lts-empty">
          <h5>ℹ️ Aucune fiche technique disponible</h5>
          <p>Vous n'avez pas encore créé de fiche technique.</p>
        </div>
      )}

      {/* MODAL */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)} 
        centered
        size="xl"
        className="lts-modal"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="d-flex flex-column">
            <div className="lts-modal-title">📋 Détails Complets de la Fiche Technique</div>
            <small className="text-white-50 lts-modal-subtitle">
              {selectedSheet && getProductNameById(selectedSheet.product)}
            </small>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="lts-modal-body">
          {selectedSheet && (
            <>
              {/* En-tête de la fiche */}
              <div className="card mb-4 lts-sheet-header">
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <div className="card border-primary">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-2 text-muted">Produit</h6>
                          <h5 className="card-title text-primary">
                            {getProductNameById(selectedSheet.product)}
                          </h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <div className="card border-success">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-2 text-muted">Sous-produit</h6>
                          <h5 className="card-title text-success">
                            {getSubProductNameById(selectedSheet.product, selectedSheet.subProduct)}
                          </h5>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4 mb-3">
                      <div className="card border-warning">
                        <div className="card-body">
                          <h6 className="card-subtitle mb-2 text-muted">Localisation</h6>
                          <h5 className="card-title text-warning">
                            {getLocationName(selectedSheet.localisation)}
                          </h5>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="row mt-3">
                    <div className="col-12">
                      <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded lts-cost-summary">
                        <div>
                          <h4 className="text-danger mb-0">
                            <strong>Coût total:</strong> {totalCost.toFixed(2)} DT
                            {selectedSheet.totalCost !== totalCost && (
                              <small className="text-muted ms-2">
                                (original: {selectedSheet.totalCost?.toFixed(2)} DT)
                              </small>
                            )}
                          </h4>
                        </div>
                        <div className="d-flex gap-2">
                          <Button 
                            variant="outline-primary" 
                            onClick={() => generatePDF(selectedSheet)}
                            className="lts-btn lts-btn-outline-primary"
                          >
                            <span className="lts-btn-icon">📄</span>
                            <span className="lts-btn-text">Télécharger PDF</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bouton ajouter ligne */}
              <div className="mb-3">
                <Button 
                  variant={showAddRow ? "outline-secondary" : "outline-success"} 
                  onClick={showAddRow ? handleCancelAddRow : () => setShowAddRow(true)}
                  className="lts-btn lts-btn-add-row"
                >
                  {showAddRow ? (
                    <>
                      <span className="lts-btn-icon">−</span>
                      <span className="lts-btn-text">Annuler l'ajout</span>
                    </>
                  ) : (
                    <>
                      <span className="lts-btn-icon">+</span>
                      <span className="lts-btn-text">Ajouter un nouvel ingrédient</span>
                    </>
                  )}
                </Button>
              </div>

              {/* Formulaire d'ajout */}
              {showAddRow && (
                <div className="card mb-4 border-success lts-add-row-form">
                  <div className="card-header bg-success text-white">
                    <h5 className="mb-0 d-flex align-items-center gap-2">
                      <span className="lts-form-icon">➕</span>
                      <span>Ajouter un nouvel ingrédient</span>
                    </h5>
                  </div>
                  <div className="card-body">
                    <Form>
                      <div className="row g-3">
                        <div className="col-md-3">
                          <Form.Group>
                            <Form.Label>Famille</Form.Label>
                            <Form.Select
                              value={newRow.family}
                              onChange={(e) => handleNewRowChange('family', e.target.value)}
                              required
                              className="lts-form-control"
                            >
                              <option value="">-- Sélectionnez une famille --</option>
                              {families.map((family) => (
                                <option key={family._id} value={family._id}>
                                  {family.name}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </div>
                        
                        <div className="col-md-3">
                          <Form.Group>
                            <Form.Label>Ingrédient</Form.Label>
                            <Form.Select
                              value={newRow.ingredient}
                              onChange={(e) => handleNewRowChange('ingredient', e.target.value)}
                              disabled={!newRow.family}
                              required
                              className="lts-form-control"
                            >
                              <option value="">-- Sélectionnez un ingrédient --</option>
                              {newRow.ingredients?.map((ingredient) => (
                                <option key={ingredient._id} value={ingredient._id}>
                                  {ingredient.name} {ingredient.purchasePrice ? `(${ingredient.purchasePrice} DT)` : ''}
                                </option>
                              ))}
                            </Form.Select>
                          </Form.Group>
                        </div>
                        
                        <div className="col-md-2">
                          <Form.Group>
                            <Form.Label>Quantité</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.001"
                              min="0"
                              value={newRow.quantity}
                              onChange={(e) => handleNewRowChange('quantity', e.target.value)}
                              required
                              className="lts-form-control"
                            />
                          </Form.Group>
                        </div>
                        
                        <div className="col-md-2">
                          <Form.Group>
                            <Form.Label>Prix unitaire (DT)</Form.Label>
                            <Form.Control
                              type="number"
                              step="0.001"
                              min="0"
                              value={newRow.pricePerUnit}
                              onChange={(e) => handleNewRowChange('pricePerUnit', e.target.value)}
                              required
                              placeholder="Prix d'achat"
                              className="lts-form-control"
                            />
                            {newRow.ingredient && (
                              <small className="text-muted">
                                Le prix de l'ingrédient sera ajouté au coût total
                              </small>
                            )}
                          </Form.Group>
                        </div>
                        
                        <div className="col-md-2">
                          <Form.Group>
                            <Form.Label>Coût (DT)</Form.Label>
                            <Form.Control
                              type="text"
                              value={newRow.cost.toFixed(3)}
                              disabled
                              className="lts-form-control bg-light"
                            />
                          </Form.Group>
                        </div>
                        
                        <div className="col-12 mt-2">
                          <div className="d-flex gap-2">
                            <Button 
                              variant="primary" 
                              onClick={handleAddNewRow}
                              className="lts-btn lts-btn-primary"
                              disabled={!newRow.ingredient || newRow.quantity <= 0 || newRow.pricePerUnit <= 0 || isSaving}
                            >
                              {isSaving ? (
                                <>
                                  <Spinner animation="border" size="sm" className="me-2" />
                                  <span>Ajout en cours...</span>
                                </>
                              ) : (
                                <>
                                  <span className="lts-btn-icon">💾</span>
                                  <span className="lts-btn-text">Ajouter à la fiche technique</span>
                                </>
                              )}
                            </Button>
                            <Button 
                              variant="outline-secondary" 
                              onClick={handleCancelAddRow}
                              className="lts-btn lts-btn-outline-secondary"
                            >
                              <span className="lts-btn-icon">✕</span>
                              <span className="lts-btn-text">Annuler</span>
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  </div>
                </div>
              )}

              {/* Tableau des ingrédients existants */}
              <div className="card lts-ingredients-table">
                <div className="card-header bg-dark text-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">📊 Ingrédients de la fiche</h5>
                    <div className="text-warning">
                      <small>Modifications en temps réel</small>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <Table striped bordered hover className="mb-0">
                      <thead className="table-secondary">
                        <tr>
                          <th width="5%">#</th>
                          <th width="25%">Ingrédient</th>
                          <th width="15%">Famille</th>
                          <th width="15%">Quantité</th>
                          <th width="15%">Prix unitaire (DT)</th>
                          <th width="15%">Coût (DT)</th>
                          <th width="10%">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editableRows.map((row, i) => (
                          <tr key={i}>
                            <td className="text-center fw-bold">{i + 1}</td>
                            <td>{getIngredientNameById(row)}</td>
                            <td>
                              <span className="badge bg-info lts-badge">
                                {getFamilyName(row.family)}
                              </span>
                            </td>
                            <td>
                              <Form.Control
                                type="number"
                                step="0.001"
                                min="0"
                                value={row.quantity || 0}
                                onChange={(e) => handleQuantityChange(i, e.target.value)}
                                size="sm"
                                className="lts-form-control-sm"
                              />
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                <Form.Control
                                  type="number"
                                  step="0.001"
                                  min="0"
                                  value={row.pricePerUnit || 0}
                                  onChange={(e) => handlePriceChange(i, e.target.value)}
                                  size="sm"
                                  placeholder="Prix unitaire"
                                  className="lts-form-control-sm"
                                />
                                <span className="fw-bold">DT</span>
                              </div>
                            </td>
                            <td className="text-end fw-bold text-success">
                              {(row.cost || 0).toFixed(3)} DT
                            </td>
                            <td className="text-center">
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteRow(row._id)}
                                title="Supprimer cette ligne"
                                disabled={!row._id || isSaving}
                                className="lts-btn lts-btn-sm lts-btn-outline-danger"
                              >
                                {isSaving ? (
                                  <Spinner animation="border" size="sm" />
                                ) : (
                                  <span className="lts-btn-icon">🗑️</span>
                                )}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="table-primary">
                          <td colSpan="5" className="text-end fw-bold">
                            Coût total mis à jour:
                          </td>
                          <td className="text-end fw-bold text-danger">
                            {totalCost.toFixed(2)} DT
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="lts-modal-footer">
          <Button 
            variant="secondary" 
            onClick={() => setShowModal(false)}
            className="lts-btn lts-btn-secondary"
          >
            Fermer
          </Button>
          <Button 
            variant="primary" 
            onClick={savePriceUpdates}
            disabled={isSaving}
            className="lts-btn lts-btn-primary"
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                <span>Enregistrement...</span>
              </>
            ) : (
              <>
                <span className="lts-btn-icon">💾</span>
                <span className="lts-btn-text">Enregistrer toutes les modifications</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ListTechnicalSheets;